import { useCallback, type ChangeEvent } from "react";
import { Link } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { FeedQueryPreview } from "../../../../components/domain/data-display/FeedQueryPreview";
import { PubmedQueryInput } from "../../../../components/domain/inputs/PubmedQueryInput";
import { gaps } from "../../../../components/primitives/spacing";
import { Panel } from "../../../../components/primitives/surfaces/Panel";
import {
  CORPUS_FEED_DESCRIPTION_MAX_LENGTH,
  CORPUS_FEED_MAX_QUERY_MATCHES,
  CORPUS_FEED_NAME_MAX_LENGTH,
  PUBMED_INGEST_MAX_RESULTS,
} from "@shared/types/corpus/api.ts";
import { useNewFeedForm } from "./useNewFeedForm";
import { ROUTES } from "../../../../route_paths";

/** The fields that make a feed beside a preview of what its query would bring. The query is
 *  validated before the feed can be created, and any edit to it asks for that again. */
export function NewFeedForm() {
  const form = useNewFeedForm();
  const { validation, create } = form;

  const handleNameChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => form.setName(e.target.value),
    [form]
  );
  const handleDescriptionChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => form.setDescription(e.target.value),
    [form]
  );

  return (
    <Grid container spacing={gaps.base}>
      <Grid size={{ xs: 12, md: 7 }}>
        <Panel>
          <Stack gap={gaps.base}>
            <TextField
              label="Name"
              value={form.name}
              onChange={handleNameChange}
              slotProps={{ htmlInput: { maxLength: CORPUS_FEED_NAME_MAX_LENGTH } }}
              required
              autoFocus
            />
            <TextField
              label="Description (optional)"
              placeholder="What this feed is for"
              value={form.description}
              onChange={handleDescriptionChange}
              slotProps={{ htmlInput: { maxLength: CORPUS_FEED_DESCRIPTION_MAX_LENGTH } }}
            />
            <PubmedQueryInput
              label="PubMed query"
              value={validation.query}
              onChange={validation.setQuery}
              minRows={3}
              error={validation.syntaxProblem !== null}
              helperText={validation.syntaxProblem ?? "Query can't be changed after creation"}
            />

            {!form.extractionWarm && (
              <Alert severity="info">
                Kodakku is still starting up — this should only take a few seconds. A query cannot
                be validated until it&apos;s ready.
              </Alert>
            )}
            {validation.error && <Alert severity="error">{validation.error}</Alert>}
            {create.error && <Alert severity="error">{create.error}</Alert>}

            <Stack direction="row" gap={gaps.snug} justifyContent="flex-end">
              <Button component={Link} to={ROUTES.internal.library.feeds.path()} variant="outlined">
                Cancel
              </Button>
              {validation.preview ? (
                <Button
                  variant="contained"
                  onClick={create.handle}
                  disabled={!form.canCreate}
                  loading={create.loading}
                >
                  Create
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={validation.validate}
                  disabled={!validation.canValidate}
                  loading={validation.validating}
                >
                  Validate query
                </Button>
              )}
            </Stack>
          </Stack>
        </Panel>
      </Grid>
      <Grid size={{ xs: 12, md: 5 }}>
        <FeedQueryPreview
          preview={validation.preview}
          runCap={PUBMED_INGEST_MAX_RESULTS}
          matchLimit={CORPUS_FEED_MAX_QUERY_MATCHES}
        />
      </Grid>
    </Grid>
  );
}
